var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a;
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Drawer, IconButton, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Stack, TextField, Typography, } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import DrawIcon from "@mui/icons-material/Draw";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import { AuthContext } from "@/providers/AuthContextProvider";
import { validatePanel } from "@/lib/signing/api";
import { SignBlock, isViewer, signBlock, signerCertificate, signerName } from "@/lib/signing/request";
import { requestStatusKey, shownRequestStatus } from "@/lib/signing/status";
import { SigningRequestStatus } from "@/lib/signing/types";
import { SigningDialog } from "./SigningDialog";
import { SigningHandoverLauncher } from "./SigningHandoverLauncher";
import { SigningSubject } from "./SigningSubject";
import { actionDescription, requestTitle, useSigningFormat } from "./format";
import { useSettlePolling } from "./useSettlePolling";
import { useSigningPermissions } from "./useSigningPermissions";
var STATUS_COLOR = (_a = {},
    _a[SigningRequestStatus.Waiting] = "warning",
    _a[SigningRequestStatus.Completed] = "success",
    _a[SigningRequestStatus.Executed] = "success",
    _a[SigningRequestStatus.Cancelled] = "default",
    _a[SigningRequestStatus.Expired] = "default",
    _a[SigningRequestStatus.Failed] = "error",
    _a);
var CancelRequestDialog = function (_a) {
    var open = _a.open, onClose = _a.onClose, onConfirm = _a.onConfirm;
    var t = useTranslation().t;
    var titleId = useId();
    var _b = useState(""), reason = _b[0], setReason = _b[1];
    var _c = useState(false), busy = _c[0], setBusy = _c[1];
    var _d = useState(false), failed = _d[0], setFailed = _d[1];
    var confirm = function () { return __awaiter(void 0, void 0, void 0, function () {
        var _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setBusy(true);
                    setFailed(false);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, onConfirm(reason.trim())];
                case 2:
                    _b.sent();
                    setReason("");
                    return [3 /*break*/, 5];
                case 3:
                    _a = _b.sent();
                    setFailed(true);
                    return [3 /*break*/, 5];
                case 4:
                    setBusy(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Dialog, { open: open, onClose: function () { return !busy && onClose(); }, "aria-labelledby": titleId, children: [_jsx(DialogTitle, { id: titleId, children: t("signing.widget.cancelDialog.title") }), _jsxs(DialogContent, { children: [_jsx(Typography, { sx: { mb: 2 }, children: t("signing.widget.cancelDialog.body") }), _jsx(TextField, { label: t("signing.widget.cancelDialog.reason"), value: reason, onChange: function (event) { return setReason(event.target.value); }, fullWidth: true, multiline: true, minRows: 2 }), failed ? (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: t("signing.widget.cancelDialog.error") })) : null] }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: onClose, disabled: busy, children: t("signing.widget.cancelDialog.back") }), _jsx(Button, { variant: "contained", color: "error", disabled: busy, onClick: function () {
                            confirm().catch(function () { return undefined; });
                        }, children: t("signing.widget.cancelDialog.confirm") })] })] }));
};
var SignerList = function (_a) {
    var data = _a.data;
    var t = useTranslation().t;
    var format = useSigningFormat(data.time_zone);
    var userId = useContext(AuthContext).userId;
    return (_jsx(List, { disablePadding: true, "aria-label": t("signing.panel.signers"), children: data.signers.map(function (signer) { return (_jsxs(ListItem, { divider: true, disableGutters: true, "data-signer": signer.username, children: [_jsx(ListItemIcon, { sx: { minWidth: 36 }, children: signer.signed_at ? (_jsx(CheckCircleIcon, { color: "success", "aria-hidden": true })) : (_jsx(HourglassEmptyIcon, { color: "action", "aria-hidden": true })) }), _jsx(ListItemText, { sx: { flex: "1 1 50%" }, primary: isViewer(signer, userId)
                        ? "".concat(signerName(signer), " ").concat(t("signing.panel.you"))
                        : signerName(signer), secondary: signer.title, slotProps: { primary: { sx: { fontWeight: 600 } } } }), _jsx(ListItemText, { sx: { flex: "1 1 50%" }, primary: signer.signed_at
                        ? t("signing.panel.signedAt", {
                            time: format.time(signer.signed_at),
                        })
                        : t("signing.panel.notSigned"), secondary: signer.signed_at && signerCertificate(signer)
                        ? t("signing.panel.certificate", {
                            name: signerCertificate(signer),
                        })
                        : null })] }, signer.user_id)); }) }));
};
var StatusLine = function (_a) {
    var data = _a.data;
    var t = useTranslation().t;
    var format = useSigningFormat(data.time_zone);
    var request = data.request, count = data.count;
    var counted = request.status !== SigningRequestStatus.Cancelled;
    // Named as the Signatures tab's Requests list names it.
    var shown = shownRequestStatus(request, new Date());
    var status = t(requestStatusKey(shown));
    var label = counted
        ? "".concat(status, " \u00B7 ").concat(t("signing.panel.progress", { count: count, total: request.required }))
        : status;
    var note = request.status === SigningRequestStatus.Waiting && request.expires_at
        ? t("signing.panel.expiresAt", { time: format.time(request.expires_at) })
        : (request.status === SigningRequestStatus.Completed ||
            request.status === SigningRequestStatus.Executed) &&
            request.completed_at
            ? t("signing.widget.panel.completedAt", {
                time: format.time(request.completed_at),
            })
            : request.status === SigningRequestStatus.Cancelled && request.cancel_reason
                ? t("signing.cancelReasons.".concat(request.cancel_reason))
                : null;
    return (_jsxs(Stack, { direction: "row", spacing: 1.5, sx: { alignItems: "center", flexWrap: "wrap" }, children: [_jsx(Chip, { label: label, color: STATUS_COLOR[shown], variant: shown === SigningRequestStatus.Waiting ? "outlined" : "filled", size: "small", "data-testid": "signing-status", 
                // The outlined warning colour is too light for text; the border carries it.
                sx: shown === SigningRequestStatus.Waiting ? { color: "text.primary" } : undefined }), note ? _jsx(Typography, { color: "text.secondary", children: note }) : null] }));
};
/**
 * A signing request in a right-hand drawer: its status and progress, the rule,
 * what is signed, the signing code and the signers, with Sign, Next member
 * signs in and Cancel request while it waits.
 */
export var SigningRequestPanel = function (_a) {
    var requestId = _a.requestId, api = _a.api, open = _a.open, onClose = _a.onClose, _b = _a.autoSign, autoSign = _b === void 0 ? false : _b, completionActions = _a.completionActions, onChange = _a.onChange, storage = _a.storage, expectedEventId = _a.expectedEventId;
    var t = useTranslation().t;
    var userId = useContext(AuthContext).userId;
    var permissions = useSigningPermissions();
    var titleId = useId();
    var _c = useState(null), data = _c[0], setData = _c[1];
    var _d = useState(false), loadFailed = _d[0], setLoadFailed = _d[1];
    var _e = useState(false), dialogOpen = _e[0], setDialogOpen = _e[1];
    var _f = useState(false), cancelOpen = _f[0], setCancelOpen = _f[1];
    var autoSignDone = useRef(false);
    var load = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var loaded, _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    setLoadFailed(false);
                    _c.label = 1;
                case 1:
                    _c.trys.push([1, 3, , 4]);
                    _a = validatePanel;
                    return [4 /*yield*/, api.getRequest(requestId)];
                case 2:
                    loaded = _a.apply(void 0, [_c.sent()]);
                    setData(loaded);
                    return [2 /*return*/, loaded];
                case 3:
                    _b = _c.sent();
                    setLoadFailed(true);
                    return [2 /*return*/, null];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [api, requestId]);
    useEffect(function () {
        setData(null);
        setDialogOpen(false);
        autoSignDone.current = false;
        if (open) {
            load().catch(function () { return undefined; });
        }
    }, [open, load]);
    var block = data
        ? signBlock(data, userId, permissions.canSign(data.request.action))
        : SignBlock.Closed;
    useEffect(function () {
        if (!data || autoSignDone.current)
            return;
        autoSignDone.current = true;
        if (expectedEventId && data.request.election_event_id !== expectedEventId) {
            onClose();
            return;
        }
        if (autoSign && block === SignBlock.None)
            setDialogOpen(true);
    }, [autoSign, data, block, expectedEventId, onClose]);
    var refresh = function () { return __awaiter(void 0, void 0, void 0, function () {
        var loaded;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, load()];
                case 1:
                    loaded = _a.sent();
                    if (loaded)
                        onChange === null || onChange === void 0 ? void 0 : onChange(loaded);
                    return [2 /*return*/];
            }
        });
    }); };
    // The last signature dispatched the action: follow it until it ran or failed.
    useSettlePolling(data === null || data === void 0 ? void 0 : data.request.status, open, function () { return __awaiter(void 0, void 0, void 0, function () {
        var loaded;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, load()];
                case 1:
                    loaded = _a.sent();
                    if (loaded && loaded.request.status !== SigningRequestStatus.Completed) {
                        onChange === null || onChange === void 0 ? void 0 : onChange(loaded);
                    }
                    return [2 /*return*/];
            }
        });
    }); });
    var request = data === null || data === void 0 ? void 0 : data.request;
    var waiting = (request === null || request === void 0 ? void 0 : request.status) === SigningRequestStatus.Waiting;
    var isRequester = !!request && request.requested_by === userId;
    var canHandOver = waiting && !!request && (isRequester || permissions.canSign(request.action));
    var canCancel = waiting && (isRequester || permissions.canCancel);
    var completed = (request === null || request === void 0 ? void 0 : request.status) === SigningRequestStatus.Completed ||
        (request === null || request === void 0 ? void 0 : request.status) === SigningRequestStatus.Executed;
    return (_jsxs(Drawer, { anchor: "right", open: open, onClose: onClose, sx: {
            "& .MuiDrawer-paper": {
                width: { xs: "100%", sm: 640 },
                maxWidth: "100%",
                boxSizing: "border-box",
            },
        }, slotProps: {
            paper: {
                "aria-labelledby": titleId,
                "sx": { p: { xs: 2, sm: 3 } },
            },
        }, children: [_jsxs(Stack, { direction: "row", sx: { alignItems: "flex-start", gap: 1 }, children: [_jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsx(Typography, { id: titleId, variant: "h5", component: "h2", children: data ? requestTitle(t, data) : t("signing.widget.loading") }), data ? (_jsx(Typography, { color: "text.secondary", children: actionDescription(t, data.request.action, data.seals_ballots) })) : null] }), _jsx(IconButton, { onClick: onClose, "aria-label": t("signing.widget.panel.close"), children: _jsx(CloseIcon, {}) })] }), !data ? (loadFailed ? (_jsx(Alert, { severity: "error", sx: { mt: 3 }, action: _jsx(Button, { color: "inherit", size: "small", onClick: function () {
                        load().catch(function () { return undefined; });
                    }, children: t("signing.widget.retry") }), children: t("signing.widget.loadError") })) : (_jsx(Box, { sx: { display: "flex", justifyContent: "center", mt: 6 }, children: _jsx(CircularProgress, { "aria-label": t("signing.widget.loading") }) }))) : (_jsxs(Stack, { spacing: 3, sx: { mt: 3 }, children: [_jsx(StatusLine, { data: data }), data.request.status !== SigningRequestStatus.Cancelled ? (_jsxs(Stack, { direction: "row", spacing: 2, sx: { alignItems: "center" }, children: [_jsx(LinearProgress, { variant: "determinate", value: Math.min(100, (100 * data.count) / Math.max(1, data.request.required)), color: completed ? "success" : "primary", sx: { flex: 1, height: 6, borderRadius: 3 }, "aria-label": t("signing.panel.progress", {
                                    count: data.count,
                                    total: data.request.required,
                                }) }), _jsx(Typography, { sx: { fontWeight: 700 }, children: t("signing.panel.progress", {
                                    count: data.count,
                                    total: data.request.required,
                                }) })] })) : null, _jsx(Typography, { children: data.election_name
                            ? t("signing.panel.rulePost", {
                                n: data.request.required,
                                post: data.election_name,
                            })
                            : t("signing.panel.ruleEvent", { n: data.request.required }) }), _jsx(SigningSubject, { data: data, api: api }), _jsx(Divider, {}), _jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", component: "h3", sx: { fontWeight: 700, mb: 1 }, children: t("signing.panel.signers") }), _jsx(SignerList, { data: data })] }), data.request.status === SigningRequestStatus.Cancelled ? (_jsx(Alert, { severity: "warning", children: t("signing.dialog.problems.cancelled", {
                            reason: data.request.cancel_reason
                                ? t("signing.cancelReasons.".concat(data.request.cancel_reason))
                                : t("signing.status.cancelled"),
                        }) })) : data.request.status === SigningRequestStatus.Expired ? (_jsx(Alert, { severity: "info", children: t("signing.widget.panel.expired") })) : data.request.status === SigningRequestStatus.Failed ? (_jsx(Alert, { severity: "error", children: t("signing.widget.panel.failed") })) : null, waiting ? (_jsxs(Stack, { direction: "row", spacing: 1.5, sx: { flexWrap: "wrap", rowGap: 1.5 }, children: [block === SignBlock.None ? (_jsx(Button, { variant: "contained", startIcon: _jsx(DrawIcon, {}), onClick: function () { return setDialogOpen(true); }, children: t("signing.panel.sign") })) : null, canHandOver ? (_jsx(SigningHandoverLauncher, { data: data, api: api, storage: storage })) : null, canCancel ? (_jsx(Button, { color: "inherit", onClick: function () { return setCancelOpen(true); }, children: t("signing.panel.cancel") })) : null] })) : null, completed && completionActions ? (_jsx(Stack, { direction: "row", spacing: 1.5, sx: { flexWrap: "wrap", rowGap: 1.5 }, children: completionActions(data) })) : null, _jsx(SigningDialog, { open: dialogOpen, data: data, api: api, onClose: function () { return setDialogOpen(false); }, onSigned: function () {
                            refresh().catch(function () { return undefined; });
                        }, onRequestChanged: function () {
                            refresh().catch(function () { return undefined; });
                        } }), _jsx(CancelRequestDialog, { open: cancelOpen, onClose: function () { return setCancelOpen(false); }, onConfirm: function (reason) { return __awaiter(void 0, void 0, void 0, function () {
                            return __generator(this, function (_a) {
                                switch (_a.label) {
                                    case 0: return [4 /*yield*/, api.cancel(data.request.id, reason ? { reason: reason } : {})];
                                    case 1:
                                        _a.sent();
                                        setCancelOpen(false);
                                        return [4 /*yield*/, refresh()];
                                    case 2:
                                        _a.sent();
                                        return [2 /*return*/];
                                }
                            });
                        }); } })] }))] }));
};
